import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { BOARD_IMPACT_TUNING } from "../tuning/featureTuning";
import type { Point } from "./flightPath";
import { BOARD_IMPACT_LAYOUT, handoffFrameUnits } from "./layout";

/**
 * The artist's board impact (`board_impact.riv`, played as its Spine conversion): its `hit` trigger plays GROW's blue
 * explosion once (1.1 s; again mid-burst, from its start): a flash, a plasma orb bursting into an energy shell,
 * shockwaves, rings of blue fire, waves racing sideways to the frame's sides, smoke, motes and sparks. Idle, it draws
 * nothing.
 */
export const BOARD_IMPACT_RIVE = { trigger: "hit" } as const;

/** What the board impact asks of its art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface BoardImpactArt {
  readonly texture: Texture;
  resize(scale: number): void;
  trigger(name: string): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/**
 * GROW's explosion on the board (the artist's handoff): as GROW's fire streak lands on the board's centre it bursts
 * there, over the board, the coins and the frame's fire, drawn to the game's frame as the edge fire is (its side waves
 * reach the frame's sides). Shown and drawn on the UI's runner for its burst, at the tuning's frame rate, then hidden:
 * never work a round waits for. Nothing plays before it is in.
 */
export class BoardImpact extends Container {
  private readonly ambientRunner: PresentationTaskRunner;
  private readonly log: Log;
  private art: BoardImpactArt | null = null;
  private sprite: Sprite | null = null;
  private hits = 0;
  /** Engine seconds of its burst still to draw (0: idle, hidden). */
  private left = 0;
  private drawing = false;
  /** Device pixels per design unit where it plays (its texture's size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { ambientRunner: PresentationTaskRunner; log: Log }) {
    super({ label: "BoardImpact" });
    this.ambientRunner = input.ambientRunner;
    this.log = input.log;
  }

  /** The artist's board impact, loaded: its hit point where it bursts, drawn to the game's frame, hidden until then. */
  public setArt(art: BoardImpactArt): void {
    if (this.destroyed || this.art) {
      art.dispose();
      return;
    }
    const { artboard, hit } = BOARD_IMPACT_LAYOUT;
    const units = this.units;
    art.resize(this.textureScale());
    const sprite = new Sprite({ texture: art.texture, anchor: { x: hit.x / artboard.width, y: hit.y / artboard.height }, label: "BoardImpact" });
    sprite.setSize(artboard.width * units, artboard.height * units);
    sprite.visible = false;
    this.addChild(sprite);
    this.art = art;
    this.sprite = sprite;
    this.log("[BoardImpact] the board impact is in");
  }

  /** Whether it is in. */
  public get ready(): boolean {
    return this.art !== null;
  }

  /** Whether it is in, how many times it has burst, and whether it bursts now (the dev hook's read). */
  public get state(): { loaded: boolean; hits: number; playing: boolean } {
    return { loaded: this.art !== null, hits: this.hits, playing: this.left > 0 };
  }

  /** Device pixels per design unit where it plays: its texture is drawn at its size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    this.art?.resize(this.textureScale());
  }

  /** It bursts on `at`, in this layer's space (again from its start if it bursts already); nothing before it is in. */
  public hit(at: Point): void {
    const { art, sprite } = this;
    if (!art || !sprite || this.destroyed) return;
    this.hits += 1;
    sprite.position.set(at.x, at.y);
    art.trigger(BOARD_IMPACT_RIVE.trigger);
    art.render(0);
    sprite.visible = true;
    this.left = BOARD_IMPACT_TUNING.seconds;
    this.draw();
  }

  public override destroy(options?: DestroyOptions): void {
    const art = this.art;
    this.art = null;
    this.sprite = null;
    art?.dispose();
    super.destroy(options);
  }

  /** Design units per artboard pixel: the artist's frame drawn to the game's frame's width. */
  private get units(): number {
    return handoffFrameUnits(BOARD_IMPACT_LAYOUT.frameWidth);
  }

  /** Texture pixels per artboard pixel: its size on the screen, at the tuning's share. */
  private textureScale(): number {
    return this.units * this.pixelsPerUnit * BOARD_IMPACT_TUNING.resolution;
  }

  /** The burst drawn at its own pace until it is over, then hidden. */
  private draw(): void {
    if (this.drawing) return;
    this.drawing = true;
    let last = 0;
    void this.ambientRunner
      .tickInScope(
        "",
        (elapsed) => {
          const delta = elapsed - last;
          last = elapsed;
          if (!this.art || this.left <= 0) return;
          this.art.advance(delta);
          this.left = Math.max(0, this.left - delta);
          if (this.left === 0 && this.sprite) this.sprite.visible = false;
        },
        () => this.destroyed || !this.art || this.left <= 0,
      )
      .then(() => {
        this.drawing = false;
        // It burst again as the last ended.
        if (!this.destroyed && this.art && this.left > 0) this.draw();
      });
  }
}
